import { useEffect } from 'react';
import { derivedPageWords } from '@xshop/shared';
import { shopWords } from '../commerce/shopWords';
import type { StoreFacts } from '../elements/render/storeFacts';
import type { PageType } from '../themes/registry';

// The browser tab, the search result and the reading direction of a store's page. index.html ships a
// placeholder ("Store", lang="en"), so every page of every store showed the same title and was marked as
// English whatever it was written in. These follow the store and the page it is showing instead.

/** The longest description a search result shows before cutting it off. */
const DESCRIPTION_LIMIT = 160;

// Strips a stored description to its first words, as a search result shows them.
const plainWords = (html: string | undefined): string =>
  (html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, DESCRIPTION_LIMIT);

/** Reads one thing a page's facts say about it: its title or its description. */
type Read = (store: StoreFacts | null) => string | undefined;

// Picks the words of the store's language for the pages every store has, English when it chose none.
const pageWords = (store: StoreFacts | null) => derivedPageWords(store?.language);
// Picks the storefront's own words in the store's language.
const ownWords = (store: StoreFacts | null) => shopWords(store?.language);

/** What each page's tab names before the store, in its language; a page missing here is named by the store alone. */
const SUBJECTS: Partial<Record<PageType, Read>> = {
  product: (store) => store?.product?.title,
  'product-cod': (store) => store?.product?.title,
  collection: (store) => store?.collection?.title,
  article: (store) => store?.page?.title,
  search: (store) => pageWords(store).search,
  cart: (store) => ownWords(store).yourCart,
  contact: (store) => store?.page?.title || ownWords(store).contact,
  'collections-list': (store) => pageWords(store).collections,
  account: (store) => pageWords(store).trackOrder,
  'not-found': (store) => pageWords(store).notFound,
  checkout: (store) => ownWords(store).checkout,
  thankyou: (store) => ownWords(store).orderConfirmed
};

/** What each page says about itself in a search result; a page missing here is described by the store's name. */
const DESCRIPTIONS: Partial<Record<PageType, Read>> = {
  product: (store) => store?.product?.description,
  'product-cod': (store) => store?.product?.description,
  collection: (store) => store?.collection?.description,
  article: (store) => store?.page?.body
};

// Names the page's tab: the product, collection or page it is about, then the store, as a shop's own pages are named.
export function documentTitle(store: StoreFacts | null, page: PageType): string {
  const name = store?.storeName.trim() || 'Store';
  const subject = SUBJECTS[page]?.(store)?.trim();
  return subject ? `${subject} – ${name}` : name;
}

// Words the page's description: what the product, collection or page says about itself, else the store's name.
export function documentDescription(store: StoreFacts | null, page: PageType): string {
  return plainWords(DESCRIPTIONS[page]?.(store)) || store?.storeName.trim() || '';
}

// Keeps the document's title, description, language and direction in step with the store and its page.
export function useDocumentHead(store: StoreFacts | null, page: PageType): void {
  const title = documentTitle(store, page);
  const description = documentDescription(store, page);
  useEffect(() => { document.title = title; }, [title]);
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (meta && description) meta.content = description;
  }, [description]);
  useEffect(() => {
    if (store?.language) document.documentElement.lang = store.language;
    if (store?.direction) document.documentElement.dir = store.direction;
  }, [store?.language, store?.direction]);
}
